import { useMutation, useQuery } from 'convex/react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../../convex/_generated/api';
import { CommunityPostCard } from './CommunityShared';
import { useTranslation } from 'react-i18next';
import { getGenreDisplayLabel, getProjectStageDisplayLabel } from './communityUtils';
import './Community.css';

export default function PublicCommunityProfile() {
  const { t } = useTranslation();
  const { handle } = useParams();
  const profileData = useQuery(api.community.getPublicProfile, handle ? { handle } : 'skip');
  const viewerState = useQuery(api.community.getViewerState, {});
  const followAuthor = useMutation(api.community.followAuthor);
  const unfollowAuthor = useMutation(api.community.unfollowAuthor);
  const savePost = useMutation(api.community.savePost);
  const unsavePost = useMutation(api.community.unsavePost);
  const createPollVote = useMutation(api.community.createPollVote);

  if (profileData === undefined) {
    return (
      <div className="profile-shell">
        <div className="profile-container" style={{ paddingTop: 80, textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="profile-shell">
        <div className="profile-container" style={{ paddingTop: 80, textAlign: 'center' }}>
          <h3>{t("profile.authorNotFound")}</h3>
          <p style={{ marginTop: 8, color: 'var(--color-text-muted)' }}>{t("profile.authorNotFoundDesc")}</p>
          <Link
            className="btn btn-primary btn-sm"
            to="/projects/community"
            style={{ marginTop: 16 }}
          >{t("profile.backToCommunity")}</Link>
        </div>
      </div>
    );
  }

  const { profile, linkedProjects, recentPosts, viewer } = profileData;
  const isOwnProfile = viewerState?.profile?.userId === profile.userId;
  const isFollowing = viewer?.isFollowing;
  const initial = (profile.displayName || 'A').charAt(0);

  const handleFollow = async () => {
    try {
      if (isFollowing) {
        await unfollowAuthor({ authorId: profile.userId });
      } else {
        await followAuthor({ authorId: profile.userId });
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="profile-shell">
      <div className="profile-container">
        {/* Back nav */}
        <header className="profile-header">
          <Link className="community-back-link" to="/projects/community">
            {t('common.back')} {t('community.title')}
          </Link>
          <div className="profile-header-links">
            <Link className="btn btn-secondary btn-sm" to="/projects">{t('common.projects')}</Link>
            {isOwnProfile && (
              <Link className="btn btn-secondary btn-sm" to="/profile">{t('profile.editProfileLink')}</Link>
            )}
          </div>
        </header>

        {/* Profile card */}
        <div className="profile-card">
          <div className="profile-card-banner">
            <div className="profile-card-avatar">
              {profile.avatar ? (
                <img src={profile.avatar} alt={profile.displayName} />
              ) : (
                initial
              )}
            </div>
          </div>
          <div className="profile-card-body">
            <div className="public-profile-top">
              <div className="profile-card-identity">
                <h2 className="profile-card-name">{profile.displayName}</h2>
                <p className="profile-card-handle">@{profile.handle}</p>
              </div>
              {!isOwnProfile && (
                <button
                  className={`btn btn-sm ${isFollowing ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={handleFollow}
                >
                  {isFollowing ? t('profile.followingLabel') : t('profile.follow')}
                </button>
              )}
            </div>

            {profile.bio && (
              <p className="public-profile-bio">{profile.bio}</p>
            )}

            <div className="public-profile-stats">
              <span><strong>{profile.followerCount}</strong> {t('profile.followers')}</span>
              <span><strong>{profile.followingCount || 0}</strong> {t('profile.following')}</span>
              <span><strong>{profile.postCount}</strong> {t('profile.posts')}</span>
            </div>

            {(profile.genreTags || []).length > 0 && (
              <div className="public-profile-tags">
                {profile.genreTags.map((tag) => (
                  <span key={tag} className="chip selected" data-genre={tag}>{getGenreDisplayLabel(t, tag)}</span>
                ))}
              </div>
            )}

            <div className="public-profile-details">
              {profile.experienceLevel && (
                <span className="public-profile-detail">📝 {profile.experienceLevel}</span>
              )}
              {profile.favoriteAuthor && (
                <span className="public-profile-detail">{t('profile.reads', { author: profile.favoriteAuthor })}</span>
              )}
            </div>
          </div>
        </div>

        {/* Linked projects */}
        {linkedProjects.length > 0 && (
          <div className="public-profile-section">
            <h3 className="public-profile-section-title">{t('common.projects')}</h3>
            <div className="public-profile-projects">
              {linkedProjects.map((project) => (
                <div key={project._id} className="public-profile-project">
                  <strong>{project.title}</strong>
                  <span>
                    {[
                      project.genre ? getGenreDisplayLabel(t, project.genre) : null,
                      project.stage ? getProjectStageDisplayLabel(t, project.stage) : null,
                    ].filter(Boolean).join(' · ') || t('profile.drafting')}
                  </span>
                  {project.synopsis && <p>{project.synopsis}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Posts */}
        <div className="public-profile-section">
          <h3 className="public-profile-section-title">
            Posts ({recentPosts.length})
          </h3>
          <div className="community-posts-list">
            {recentPosts.map((post) => (
              <CommunityPostCard
                key={post._id}
                post={post}
                viewerProfile={viewerState?.profile}
                onToggleSave={async (item) => {
                  try {
                    if (item.viewer?.isSaved) {
                      await unsavePost({ postId: item._id });
                    } else {
                      await savePost({ postId: item._id });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                }}
                onVotePoll={async (postId, optionId) => {
                  try {
                    await createPollVote({ postId, optionId });
                  } catch (e) {
                    console.error(e);
                  }
                }}
              />
            ))}
            {recentPosts.length === 0 && (
              <p className="community-empty-copy">{t('profile.noPostsFromWriter')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
